/**
 * Utility for filtering empty columns from markdown tables.
 *
 * A column is considered "empty" if ALL values in the column are either:
 * - "Not recorded" (case-insensitive, trimmed)
 * - Empty string or only whitespace
 *
 * This is applied silently (no UI message) to LLM-generated markdown tables.
 */

const EMPTY_VALUES = new Set(['not recorded', '']);

/**
 * Checks if a cell value is considered "empty" for filtering purposes.
 */
function isEmptyValue(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return EMPTY_VALUES.has(normalized);
}

interface ParsedTable {
  headerLine: string;
  separatorLine: string;
  dataLines: string[];
  startIndex: number;
  endIndex: number;
}

/**
 * Validates that header and separator lines form a valid markdown table start.
 */
function isValidTableStart(headerLine: string | undefined, separatorLine: string | undefined): boolean {
  if (!headerLine || !separatorLine) return false;
  if (!headerLine.includes('|') || !separatorLine.includes('|')) return false;

  // Separator must contain only |, -, :, and whitespace
  const separatorContent = separatorLine.replaceAll(/[\s|:-]/g, '');
  return separatorContent.length === 0;
}

/**
 * Checks if the next line looks like a table separator (indicating a new table).
 * A separator line contains only |, -, :, and whitespace characters.
 * Must include a pipe to distinguish from horizontal rules (---).
 */
function isNewTableSeparator(nextLine: string | undefined): boolean {
  if (nextLine === undefined) return false;
  const trimmed = nextLine.trim();
  if (trimmed.length === 0) return false;
  // Check each character is one of the allowed separator characters
  for (const char of trimmed) {
    if (char !== '|' && char !== '-' && char !== ':' && char !== ' ') {
      return false;
    }
  }
  // Must contain both a dash and a pipe to be a valid table separator
  // (distinguishes from horizontal rules like ---)
  return trimmed.includes('-') && trimmed.includes('|');
}

/**
 * Collects data rows from a markdown table starting after the separator.
 */
function collectDataRows(lines: string[], startDataIndex: number): { dataLines: string[]; endIndex: number } {
  const dataLines: string[] = [];
  let endIndex = startDataIndex;

  while (endIndex < lines.length) {
    const line = lines[endIndex];
    // Stop if line doesn't look like a table row
    if (!line.includes('|') || line.trim() === '') break;
    // Stop if it looks like a new table header (followed by separator)
    if (isNewTableSeparator(lines[endIndex + 1])) break;

    dataLines.push(line);
    endIndex++;
  }

  return { dataLines, endIndex };
}

/**
 * Parses a markdown table from lines starting at a given index.
 * Returns null if the lines don't form a valid markdown table.
 */
function parseTable(lines: string[], startIndex: number): ParsedTable | null {
  if (startIndex >= lines.length) return null;

  const headerLine = lines[startIndex];
  const separatorLine = lines[startIndex + 1];

  if (!isValidTableStart(headerLine, separatorLine)) return null;

  const { dataLines, endIndex } = collectDataRows(lines, startIndex + 2);

  return {
    headerLine,
    separatorLine,
    dataLines,
    startIndex,
    endIndex,
  };
}

/**
 * Splits a markdown table row into cells, handling escaped pipes (\|).
 */
function splitRow(row: string): string[] {
  const cells: string[] = [];
  const trimmed = row.trim();
  let cell = '';
  let escaped = false;

  for (const char of trimmed) {
    if (char === '|' && !escaped) {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += char;
    }
    escaped = char === '\\' && !escaped;
  }
  cells.push(cell.trim());

  // Remove empty cells from leading/trailing pipes
  if (trimmed.startsWith('|')) {
    cells.shift();
  }
  if (cells.length > 0 && cells.at(-1) === '' && trimmed.endsWith('|')) {
    cells.pop();
  }

  return cells;
}

/**
 * Joins cells back into a markdown table row.
 */
function joinRow(cells: string[]): string {
  return `| ${cells.join(' | ')} |`;
}

/**
 * Filters out columns where ALL data values are empty.
 * Returns the filtered table lines.
 */
function filterTableColumns(table: ParsedTable): string[] {
  const headerCells = splitRow(table.headerLine);
  const separatorCells = splitRow(table.separatorLine);
  const dataRows = table.dataLines.map(splitRow);

  // Find column indices where ALL values are empty
  const emptyColumnIndices = new Set<number>();

  for (let colIndex = 0; colIndex < headerCells.length; colIndex++) {
    const allEmpty = dataRows.every((row) => {
      const cellValue = row[colIndex] ?? '';
      return isEmptyValue(cellValue);
    });

    if (allEmpty && dataRows.length > 0) {
      emptyColumnIndices.add(colIndex);
    }
  }

  // If no columns to filter, return original
  if (emptyColumnIndices.size === 0) {
    return [table.headerLine, table.separatorLine, ...table.dataLines];
  }

  // Don't filter if it would remove ALL columns
  if (emptyColumnIndices.size === headerCells.length) {
    return [table.headerLine, table.separatorLine, ...table.dataLines];
  }

  // Filter out empty columns
  const filterCells = (cells: string[]): string[] =>
    cells.filter((_, index) => !emptyColumnIndices.has(index));

  const filteredHeader = joinRow(filterCells(headerCells));
  const filteredSeparator = joinRow(filterCells(separatorCells));
  const filteredDataRows = dataRows.map((row) => joinRow(filterCells(row)));

  return [filteredHeader, filteredSeparator, ...filteredDataRows];
}

/**
 * Checks if a line starts a fenced code block.
 */
function isFenceStart(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('```') || trimmed.startsWith('~~~');
}

/**
 * Checks if a line ends a fenced code block (matches the opening fence).
 */
function isFenceEnd(line: string, fenceChar: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith(fenceChar.repeat(3));
}

/**
 * Processes a line that starts a code block.
 */
function handleCodeBlockStart(
  line: string,
  result: string[],
): string {
  result.push(line);
  return line.trim().startsWith('```') ? '`' : '~';
}

/**
 * Processes a line inside a code block.
 * Returns the updated fence character (empty string if block ended).
 */
function handleCodeBlockLine(
  line: string,
  fenceChar: string,
  result: string[],
): string {
  result.push(line);
  return isFenceEnd(line, fenceChar) ? '' : fenceChar;
}

/**
 * Processes a line outside code blocks, checking for tables.
 * Returns the new line index after processing.
 */
function handleContentLine(
  lines: string[],
  index: number,
  result: string[],
): number {
  const table = parseTable(lines, index);

  if (table && table.dataLines.length > 0) {
    const filteredLines = filterTableColumns(table);
    result.push(...filteredLines);
    return table.endIndex;
  }

  result.push(lines[index]);
  return index + 1;
}

/**
 * Filters empty columns from all markdown tables in a text content.
 * Skips tables inside fenced code blocks.
 *
 * @param content - The markdown content containing tables
 * @returns The content with empty columns removed from tables
 */
export function filterMarkdownTableEmptyColumns(content: string): string {
  const lines = content.split('\n');
  const result: string[] = [];
  let i = 0;
  let inCodeBlock = false;
  let fenceChar = '';

  while (i < lines.length) {
    const line = lines[i];

    if (!inCodeBlock && isFenceStart(line)) {
      fenceChar = handleCodeBlockStart(line, result);
      inCodeBlock = true;
      i++;
      continue;
    }

    if (inCodeBlock) {
      fenceChar = handleCodeBlockLine(line, fenceChar, result);
      inCodeBlock = fenceChar !== '';
      i++;
      continue;
    }

    i = handleContentLine(lines, i, result);
  }

  return result.join('\n');
}
